import React, { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import ReactSelect, { SingleValue } from "react-select";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/AuthContext";
import {
  canPriorizarMontagemUser,
  hasFullAccessUser,
} from "../../lib/screenAccess";
import { useLojas } from "../../hooks/useLojas";
import { useFilaIntegracaoEvents } from "../../hooks/useFilaIntegracaoEvents";
import { useFornoEvents } from "../../hooks/useFornoEvents";
import { FILA_INTEGRACAO_SSE_QUERY_KEYS } from "../../constants/rankingEventQueryKeys";
import Pagination from "../../components/Pagination";
import {
  OperacaoDiaPedidoRow,
  painelService,
  SEQUENCIA_DIA_PAGE_SIZE,
} from "../../services/painelService";
import {
  filaPizzasService,
  prioridadeMontagemKey,
  type PrioridadeMontagemAlvo,
} from "../../services/filaPizzasService";
import { BrazilDate } from "../../utils/BrazilDate";
import {
  LABEL_CLASS,
  SELECT_STYLES,
} from "../../components/DateFilters/constants";
import { SequenciaPedidoCard } from "./components/SequenciaPedidoCard";
import { SequenciaPedidoDetalhesModal } from "./components/SequenciaPedidoDetalhesModal";
import type { PainelOrigemFiltro } from "../Painel/utils/origemPainel";

interface LojaOption {
  value: number;
  label: string;
}

const SEQUENCIA_PAGINATION_CLASS =
  "rounded-lg border border-neutral-200 bg-white px-4 py-3 shadow-sm sm:px-6";

function resolvePrioridadeAlvoRow(
  row: OperacaoDiaPedidoRow
): PrioridadeMontagemAlvo | null {
  if (row.canalOrigem === "salao") {
    return row.pedidoFoodflyId != null
      ? { pedidoFoodflyId: row.pedidoFoodflyId }
      : null;
  }
  return row.pedidoIntegracaoId > 0
    ? { pedidoIntegracaoId: row.pedidoIntegracaoId }
    : null;
}

const SequenciaPedidosPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = hasFullAccessUser(user);

  const { data: lojasData } = useLojas();
  const lojas = useMemo(() => lojasData ?? [], [lojasData]);

  const [selectedLojaId, setSelectedLojaId] = useState<number | undefined>(
    undefined
  );
  const [pedidoBusca, setPedidoBusca] = useState("");
  const [priorizandoKey, setPriorizandoKey] = useState<string | null>(null);
  const [removendoKey, setRemovendoKey] = useState<string | null>(null);
  const [prioridades, setPrioridades] = useState<string[]>([]);
  const [tick, setTick] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [detalhesRow, setDetalhesRow] = useState<OperacaoDiaPedidoRow | null>(
    null
  );
  const [origemFiltro, setOrigemFiltro] = useState<PainelOrigemFiltro>("todos");

  const effectiveLojaId = isAdmin
    ? (selectedLojaId ?? user?.lojaId)
    : user?.lojaId;

  const displayIdFiltro = useMemo(() => {
    const digits = pedidoBusca.replace(/\D/g, "").trim();
    if (!digits) return undefined;
    const n = Number(digits);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [pedidoBusca]);

  useEffect(() => {
    if (selectedLojaId === undefined && user?.lojaId != null) {
      setSelectedLojaId(user.lojaId);
    }
  }, [user?.lojaId, selectedLojaId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [effectiveLojaId, displayIdFiltro, origemFiltro]);

  const canalOrigemApi =
    origemFiltro === "todos" ? undefined : origemFiltro;

  const lojaOptions = useMemo<LojaOption[]>(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const selectedLojaOption =
    lojaOptions.find((o) => o.value === selectedLojaId) ?? null;

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["sequencia-dia", effectiveLojaId],
    });
  }, [queryClient, effectiveLojaId]);

  useFilaIntegracaoEvents(effectiveLojaId, {
    queryKeys: FILA_INTEGRACAO_SSE_QUERY_KEYS.sequenciaPedidos,
  });
  useFornoEvents(invalidate, effectiveLojaId);

  const {
    data: sequenciaData,
    isLoading,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: [
      "sequencia-dia",
      effectiveLojaId,
      currentPage,
      displayIdFiltro ?? null,
      canalOrigemApi ?? null,
    ],
    queryFn: () =>
      painelService.getSequenciaDia({
        lojaId: effectiveLojaId,
        page: currentPage,
        limit: SEQUENCIA_DIA_PAGE_SIZE,
        displayId: displayIdFiltro,
        canalOrigem: canalOrigemApi,
      }),
    enabled: effectiveLojaId != null,
    refetchInterval: 8000,
    placeholderData: keepPreviousData,
  });

  const rows = useMemo(() => sequenciaData?.data ?? [], [sequenciaData?.data]);
  const totalItems = sequenciaData?.total ?? 0;
  const totalPages = sequenciaData?.totalPages ?? 1;

  useEffect(() => {
    if (sequenciaData == null) return;
    if (currentPage > sequenciaData.totalPages) {
      setCurrentPage(sequenciaData.totalPages);
    }
  }, [currentPage, sequenciaData]);

  useEffect(() => {
    if (effectiveLojaId == null) return;
    filaPizzasService
      .getPrioridadesMontagem({ lojaId: effectiveLojaId })
      .then(setPrioridades)
      .catch(() => setPrioridades([]));
  }, [effectiveLojaId, rows]);

  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const prioridadeSet = useMemo(() => new Set(prioridades), [prioridades]);
  const isRowPrioritario = (row: OperacaoDiaPedidoRow): boolean => {
    const alvo = resolvePrioridadeAlvoRow(row);
    return (
      alvo != null &&
      (row.isPrioritario || prioridadeSet.has(prioridadeMontagemKey(alvo)))
    );
  };

  const rowsComTick = useMemo(() => {
    void tick;
    const now = Date.now();
    return rows.map((r) => {
      if (r.paradoDesde == null || r.etapa === "finalizado") return r;
      const paradoSegundos = Math.max(
        0,
        Math.floor(
          (now - BrazilDate.parseAsBrazil(r.paradoDesde).getTime()) / 1000
        )
      );
      let alertaNivel = r.alertaNivel;
      const min = paradoSegundos / 60;
      if (min >= 20) alertaNivel = "critico";
      else if (min >= 10) alertaNivel = "alerta";
      else if (min >= 5) alertaNivel = "atencao";
      else alertaNivel = "ok";
      return { ...r, paradoSegundos, alertaNivel };
    });
  }, [rows, tick]);

  const resumo = useMemo(() => {
    const ativos = rowsComTick.filter(
      (r) => r.etapa !== "finalizado" && r.etapa !== "cancelado"
    );
    const parados = ativos.filter((r) => (r.paradoSegundos ?? 0) >= 5 * 60);
    const criticos = ativos.filter((r) => r.alertaNivel === "critico");
    return {
      ativos: ativos.length,
      parados: parados.length,
      criticos: criticos.length,
    };
  }, [rowsComTick]);

  const priorizar = async (alvo: PrioridadeMontagemAlvo) => {
    try {
      setPriorizandoKey(prioridadeMontagemKey(alvo));
      await filaPizzasService.priorizarMontagem(alvo);
      toast.success("Pedido priorizado na montagem (15 min)");
      const p = await filaPizzasService.getPrioridadesMontagem({
        lojaId: effectiveLojaId,
      });
      setPrioridades(p);
      invalidate();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao priorizar");
    } finally {
      setPriorizandoKey(null);
    }
  };

  const removerPrioridade = async (alvo: PrioridadeMontagemAlvo) => {
    try {
      setRemovendoKey(prioridadeMontagemKey(alvo));
      await filaPizzasService.removerPrioridadeMontagem(alvo);
      toast.info("Prioridade removida");
      const p = await filaPizzasService.getPrioridadesMontagem({
        lojaId: effectiveLojaId,
      });
      setPrioridades(p);
      invalidate();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Erro ao remover prioridade"
      );
    } finally {
      setRemovendoKey(null);
    }
  };

  const podePriorizar = canPriorizarMontagemUser(user);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const paginationProps =
    totalPages > 1
      ? {
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: SEQUENCIA_DIA_PAGE_SIZE,
          onPageChange: handlePageChange,
          className: SEQUENCIA_PAGINATION_CLASS,
        }
      : null;

  const detalhesAlvo = detalhesRow ? resolvePrioridadeAlvoRow(detalhesRow) : null;
  const detalhesKey = detalhesAlvo ? prioridadeMontagemKey(detalhesAlvo) : null;
  const detalhesIsPrioritario =
    detalhesRow != null &&
    detalhesKey != null &&
    (detalhesRow.isPrioritario || prioridadeSet.has(detalhesKey));

  if (effectiveLojaId == null && !isLoading) {
    return (
      <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center p-6 text-neutral-500">
        Selecione uma loja para visualizar os pedidos.
      </main>
    );
  }

  return (
    <main className="flex min-h-[calc(100vh-4rem)] flex-col p-4 md:p-6">
      <div className="mb-4 flex flex-wrap items-end gap-3">
        {isAdmin && lojas.length >= 1 && (
          <div className="flex min-w-[200px] flex-col gap-1">
            <label htmlFor="sequencia-loja" className={LABEL_CLASS}>
              Loja
            </label>
            <ReactSelect<LojaOption>
              inputId="sequencia-loja"
              value={selectedLojaOption}
              onChange={(o: SingleValue<LojaOption>) => {
                setSelectedLojaId(o?.value);
                setCurrentPage(1);
              }}
              options={lojaOptions}
              isClearable={false}
              placeholder="Selecione a loja"
              className="min-w-[200px] sm:min-w-[240px]"
              styles={SELECT_STYLES}
            />
          </div>
        )}

        <div className="flex min-w-[140px] flex-col gap-1">
          <label htmlFor="sequencia-pedido-busca" className={LABEL_CLASS}>
            Nº do pedido
          </label>
          <input
            id="sequencia-pedido-busca"
            type="text"
            inputMode="numeric"
            value={pedidoBusca}
            onChange={(e) => setPedidoBusca(e.target.value)}
            className="h-[38px] min-w-[140px] rounded-lg border border-neutral-300 bg-white px-3 text-sm text-neutral-800 outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 sm:min-w-[160px]"
            aria-label="Buscar pedido por número"
          />
        </div>

        <button
          type="button"
          onClick={() => void refetch()}
          disabled={isFetching}
          className="inline-flex h-[38px] items-center gap-2 rounded-md border border-neutral-300 bg-white px-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
        >
          <RefreshCw
            size={16}
            className={isFetching ? "animate-spin" : undefined}
          />
          Atualizar
        </button>

        <div
          className="inline-flex h-[38px] items-center gap-1 rounded-md border border-neutral-300 bg-neutral-50 p-0.5"
          role="group"
          aria-label="Filtrar origem dos pedidos"
        >
          <button
            type="button"
            onClick={() =>
              setOrigemFiltro((prev) => (prev === "salao" ? "todos" : "salao"))
            }
            className={`rounded px-2.5 py-1 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
              origemFiltro === "salao"
                ? "bg-neutral-800 text-white shadow-sm"
                : "text-neutral-600 hover:bg-white hover:text-neutral-900"
            }`}
            aria-pressed={origemFiltro === "salao"}
            title="Filtrar pedidos do salão (FoodFly)"
          >
            Salão
          </button>
          <button
            type="button"
            onClick={() =>
              setOrigemFiltro((prev) =>
                prev === "cardapio" ? "todos" : "cardapio",
              )
            }
            className={`rounded px-2.5 py-1 text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-primary ${
              origemFiltro === "cardapio"
                ? "bg-primary text-white shadow-sm"
                : "text-neutral-600 hover:bg-white hover:text-neutral-900"
            }`}
            aria-pressed={origemFiltro === "cardapio"}
            title="Filtrar pedidos do Cardápio Web"
          >
            Cardápio
          </button>
        </div>

        <div className="ml-auto flex flex-wrap justify-end gap-2 self-center text-xs">
          <span className="rounded-full bg-neutral-100 px-2.5 py-1 font-medium">
            {totalItems} pedidos
          </span>
          <span className="rounded-full bg-blue-50 px-2.5 py-1 font-medium text-blue-800">
            {resumo.ativos} em andamento
          </span>
          <span className="rounded-full bg-amber-100 px-2.5 py-1 font-medium text-amber-900">
            {resumo.parados} parados 5+ min
          </span>
          <span className="rounded-full bg-red-100 px-2.5 py-1 font-medium text-red-800">
            {resumo.criticos} críticos 20+ min
          </span>
        </div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary" />
        </div>
      ) : rowsComTick.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-lg border border-neutral-200 bg-white py-20 text-neutral-500 shadow-sm">
          {displayIdFiltro != null
            ? `Nenhum pedido #${displayIdFiltro} encontrado hoje nesta loja.`
            : origemFiltro === "salao"
              ? "Nenhum pedido do Salão hoje nesta loja."
              : origemFiltro === "cardapio"
                ? "Nenhum pedido do Cardápio hoje nesta loja."
                : "Nenhum pedido (Cardápio ou Salão) hoje nesta loja."}
        </div>
      ) : (
        <>
          {paginationProps && (
            <div className="mb-4">
              <Pagination {...paginationProps} />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rowsComTick.map((row) => (
              <SequenciaPedidoCard
                key={
                  row.canalOrigem === "salao"
                    ? `salao-${row.pedidoFoodflyId ?? row.orderId}-${row.displayId}`
                    : `web-${row.pedidoIntegracaoId}`
                }
                row={row}
                isPrioritario={isRowPrioritario(row)}
                onDetalhes={() => setDetalhesRow(row)}
              />
            ))}
          </div>

          {paginationProps && (
            <div className="mt-4">
              <Pagination {...paginationProps} />
            </div>
          )}
        </>
      )}

      <SequenciaPedidoDetalhesModal
        row={detalhesRow}
        lojaId={effectiveLojaId}
        isOpen={detalhesRow != null}
        onClose={() => setDetalhesRow(null)}
        podePriorizar={podePriorizar && detalhesAlvo != null}
        isPrioritario={detalhesIsPrioritario}
        isPriorizando={detalhesKey != null && priorizandoKey === detalhesKey}
        isRemovendo={detalhesKey != null && removendoKey === detalhesKey}
        onPriorizar={() => {
          if (detalhesAlvo) void priorizar(detalhesAlvo);
        }}
        onRemoverPrioridade={() => {
          if (detalhesAlvo) void removerPrioridade(detalhesAlvo);
        }}
      />
    </main>
  );
};

export default SequenciaPedidosPage;
